import React, { useEffect, useRef, useState } from 'react'

import {
  activeCellClass,
  Button,
  Checkbox,
  Icon,
  type SortColumnProps,
  type StandardTable,
  Tooltip,
} from '../../../module'
import { type ConfigItemType, type GroupHeader } from '../StandardTable.models'
import styles from './_mobile-table.module.scss'
import { c } from '../../../translations/LibraryTranslationService'
import { type DataItemExtended } from '../helperCheckboxFunctions'

type TableProps = React.ComponentProps<typeof StandardTable>

type Column<DataItem, StyleGeneric> = ConfigItemType<DataItem, StyleGeneric>

type MobileTableRowProps<DataItem, StyleGeneric> = {
  activeColumn: Column<DataItem, StyleGeneric>
  mainColumn: Column<DataItem, StyleGeneric>
  columns: TableProps['config']
  sortBy?: TableProps['sortBy']
  data: DataItemExtended<DataItem>
  rowIndex: number
  totalRowKey?: keyof DataItem
  scrollPositionRef: React.RefObject<HTMLDivElement | null>
  headerPositionRef: React.RefObject<HTMLDivElement | null>
  groupHeaderRefsArray?: (HTMLDivElement | null)[]
  loading?: boolean

  // Checkbox-related properties
  hasCheckboxes?: boolean
  checkboxCallout?: ({
    checkboxValue,
    checkedItemData,
    name,
    index,
  }: {
    checkboxValue: boolean
    checkedItemData: DataItemExtended<DataItem>
    name?: string
    index?: number | undefined
  }) => void
  checked?: boolean
  disabled?: boolean // Added disabled prop type
  disableCheckboxes?: (data: DataItemExtended<DataItem>) => boolean
}

const MobileTableRow = <DataItem, StyleGeneric>({
  activeColumn,
  mainColumn,
  columns,
  data,
  sortBy,
  rowIndex,
  totalRowKey,
  scrollPositionRef,
  headerPositionRef,
  hasCheckboxes,
  checkboxCallout,
  loading,
  checked,
  groupHeaderRefsArray,
  disableCheckboxes,
}: MobileTableRowProps<DataItem, StyleGeneric>): React.JSX.Element => {
  const rowPositionRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false),
    [isScrollTop, setScrollTop] = useState(false),
    // Fix: Properly filter out both mainColumn and activeColumn from transformedColumns
    transformedColumns = columns.filter(
      (c: Column<DataItem, Record<string, unknown>>) => {
        return (
          !c.mainColumn &&
          c.label !== activeColumn.label &&
          c.label !== mainColumn.label
        )
      },
    )

  type CellType = {
    options?: SortColumnProps['options']
    name?: SortColumnProps['propName']
  }
  const getCells = (column: CellType) => {
    const cells = []
    if (column.options) {
      column.options.forEach((co) => {
        cells.push(co.name)
      })
    }
    if (column.name) {
      cells.push(column.name)
    }
    return cells
  }
  //Scroll to the top when the Total row expands.
  const isTotalRowEnabled = totalRowKey ? data[totalRowKey] : false
  useEffect(() => {
    if (isScrollTop && expanded && scrollPositionRef) {
      scrollPositionRef.current?.scrollIntoView({
        behavior: 'smooth',
      })
      setScrollTop(false)
    }
  }, [isScrollTop, expanded, scrollPositionRef])

  // To get a header's dynamic height and use it to make the first row sticky.
  let calculatedHeight = {}
  if (isTotalRowEnabled && headerPositionRef) {
    const headerHeight = headerPositionRef.current?.clientHeight as number
    calculatedHeight = {
      top: headerHeight && headerHeight + 55, // 55px is calculated by header's top : 53px + 2px bottom border.
    }
  }

  let headerHeight = 0
  if (headerPositionRef) {
    headerHeight = headerPositionRef.current?.getBoundingClientRect()
      .height as number
  }

  const groupData = data as GroupHeader<DataItem>
  const getGroupElement = (groupData: GroupHeader<DataItem>) => {
    if (
      groupHeaderRefsArray &&
      groupHeaderRefsArray.length &&
      groupData.groupNum
    ) {
      return groupHeaderRefsArray[groupData.groupNum - 1]
    }
  }

  return (
    <>
      <div
        ref={rowPositionRef}
        className={`${hasCheckboxes ? styles.checkboxRow : styles.row} ${
          columns.length > 2 && expanded ? styles.expandedRow : ''
        } ${isTotalRowEnabled ? styles.totalRow : ''} `}
        onClick={() => {
          isTotalRowEnabled && setScrollTop(true)
          // Making some DOM manipulations if the header row is expanded/collapsed
          if (groupData.isGroupHeader) {
            const headerRowElement = rowPositionRef?.current
            const groupElement = getGroupElement(groupData)

            if (headerRowElement && groupElement) {
              // parent element of group which is needed later to make header row and group as siblings of each other
              const groupParentElement = groupElement.parentElement
              if (!expanded) {
                /********** Apply below styles when header row is expanded **********/
                const expandedHeaderRowStyes = {
                  position: groupElement.style.position,
                  top: groupElement.style.top,
                  zIndex: groupElement.style.zIndex,
                  backgroundColor: `var(--lighter-gray)`,
                }

                const expandedGroupStyles = {
                  position: '',
                  top: '',
                  // After expanding the header row, scroll to the extreme top of the group
                  // 151px - header row height + header height + 53px(set in `header` css class)
                  scrollMarginTop: `${
                    (rowPositionRef.current?.getBoundingClientRect().height ??
                      0) +
                    headerHeight +
                    53
                  }px`,
                }

                // Making the header row from CHILD to PREVIOUS SIBLING of group so that the header row
                // becomes independent of the group and remain sticky.
                groupParentElement?.insertBefore(headerRowElement, groupElement)

                Object.assign(headerRowElement.style, expandedHeaderRowStyes)
                Object.assign(groupElement.style, expandedGroupStyles)

                // After expanding the header row, scroll to its extreme top
                groupElement.scrollIntoView({
                  block: 'start',
                  behavior: 'smooth',
                })
              } else {
                /**********Apply below styles when header row is collapsed**********/
                const collapsedHeaderRowStyles = {
                  position: '',
                  top: '',
                  zIndex: '',
                  backgroundColor: '',
                }

                const collapsedGroupStyles = {
                  // group styles which were previously cleared, assigned back.
                  position: headerRowElement.style.position,
                  top: headerRowElement.style.top,
                  scrollMarginTop: '',
                }

                // Again making header row as FIRST CHILD of group from SIBLING.
                groupElement.insertBefore(
                  headerRowElement,
                  groupElement.firstChild,
                )

                Object.assign(headerRowElement.style, collapsedHeaderRowStyles)
                Object.assign(groupElement.style, collapsedGroupStyles)
              }
            }
          }
        }}
        style={calculatedHeight}
      >
        {hasCheckboxes ? (
          <div className={`${styles.column}`}>
            {disableCheckboxes?.(data) ? (
              <Tooltip tooltipContent={c('tooltipCheckboxTableDisabled')}>
                <Checkbox
                  name={`checkbox-${mainColumn}`}
                  stateName='table_data'
                  checked={checked}
                  disabled
                  callout={(name, value) =>
                    checkboxCallout?.({
                      checkboxValue: value,
                      checkedItemData: data,
                      name,
                      index: rowIndex,
                    })
                  }
                  label=''
                  hideLabel
                />
              </Tooltip>
            ) : (
              <Checkbox
                name={`checkbox-${mainColumn}`}
                stateName='table_data'
                checked={checked}
                disabled={loading}
                callout={(name, value) =>
                  checkboxCallout?.({
                    checkboxValue: value,
                    checkedItemData: data,
                    name,
                    index: rowIndex,
                  })
                }
                label=''
                hideLabel
              />
            )}
          </div>
        ) : null}

        <div
          onClick={() => {
            setExpanded(!expanded)
          }}
          className={`${styles.column} ${
            isTotalRowEnabled
              ? styles.lighterGrayBackground
              : sortBy
                ? activeCellClass({
                    cells: getCells(mainColumn),
                    activeName: sortBy.prop,
                  })
                : ''
          } ${
            mainColumn.cell.className
              ? typeof mainColumn.cell.className === 'string'
                ? mainColumn.cell.className
                : mainColumn.cell.className(data)
              : ''
          } `}
        >
          {mainColumn.cell?.children(
            { ...data, sortProp: sortBy?.prop },
            rowIndex,
          )}
        </div>
        <div
          onClick={() => {
            setExpanded(!expanded)
          }}
          className={`${styles.activeColumn} ${styles.column} ${
            isTotalRowEnabled
              ? styles.lighterGrayBackground
              : sortBy
                ? activeCellClass({
                    cells: getCells(activeColumn),
                    activeName: sortBy.prop,
                  })
                : ''
          }
          ${
            activeColumn.cell.className
              ? typeof activeColumn.cell.className === 'string'
                ? activeColumn.cell.className
                : activeColumn.cell.className(data)
              : ''
          }
          `}
        >
          <div
            className={columns.length > 2 ? styles.activeColumnContainer : ''}
          >
            {activeColumn.cell?.children({ ...data, sortProp: sortBy?.prop })}
            {columns.length > 2 && (
              <div className={styles.caratContainer}>
                <Button
                  as='unstyled'
                  onClick={() => {
                    setExpanded(!expanded)
                  }}
                >
                  <Icon
                    icon='caretDown'
                    className={expanded ? styles.flipped : ''}
                    iconSize='16px'
                  />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {expanded &&
        transformedColumns?.map(
          (c: Column<DataItem, Record<string, unknown>>, i: number) => (
            <div
              key={c.label}
              className={`${
                c.isButton
                  ? isTotalRowEnabled
                    ? styles.hideColumn
                    : styles.isButton
                  : styles.row
              } ${
                transformedColumns?.length - 1 === i ? styles.insetShadow : ''
              } ${
                c.cell.className
                  ? typeof c.cell.className === 'string'
                    ? c.cell.className
                    : ''
                  : ''
              } `}
            >
              {!c.isButton && (
                <div className={styles.column}>
                  {c.label}
                  {c.tooltip && (
                    <Tooltip
                      tooltipContent={c.tooltip.content}
                      position={
                        c.tooltip.position ? c.tooltip.position : 'bottom'
                      }
                    >
                      <Icon icon='info' color='dark-blue' iconSize='12px' />
                    </Tooltip>
                  )}
                </div>
              )}
              <div
                onClick={() => {
                  setExpanded(!expanded)
                }}
                className={`${styles.column} ${
                  c.cell.className
                    ? typeof c.cell.className === 'string'
                      ? ''
                      : c.cell.className(data)
                    : ''
                }`}
              >
                {c.cell?.children({ ...data, sortProp: sortBy?.prop })}
              </div>
            </div>
          ),
        )}
    </>
  )
}

export default MobileTableRow
